/* eslint-disable max-len */
import {
  Button,
  FunctionField,
  useSidebarState,
  useTranslate
} from "react-admin";
import * as User from "providers/user";
import { useEffect, useRef, useState } from "react";
import { Box, Theme, useMediaQuery } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import CustomReportingCalendar from "./CustomReportingCalendar";
import CustomDataGrid from "./CustomDataGrid";
import CustomList from "./CustomList";
import { DownloadIcon, EmptyReportIcon } from "assets/Icons";
import { useAxios } from "providers/axios";
import { useLocation } from "react-router-dom";
import { getZonedDate, getStartAndEndTime } from "utils/helpers";
import CustomEmptyList from "./CustomEmptyList";

export default function TippingList() {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const isSmall = useMediaQuery<Theme>("(max-width:599px)");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSideBarOpen, _setIsSideBarOpen] = useSidebarState();
  const [selectedView, setSelectedView] = useState<string>("day");
  const scrollRef = useRef<any>(null);
  // const dateFilterFromLocalStorage = localStorage.getItem("ReportDateFilter");
  const dateFilterFromLocalStorage = null;
  const parsedDateFilter =
    dateFilterFromLocalStorage !== null && dateFilterFromLocalStorage !== "null"
      ? dayjs(JSON.parse(dateFilterFromLocalStorage))
      : dayjs();
  const [calendarsDate, setCalendarsDate] = useState<Dayjs | null>(
    parsedDateFilter
  );
  // get page, perPage from url using react-router-dom
  const location = useLocation();

  const [selectedCalendarDate, setSelectedCalendarDate] = useState<{
    title: string;
    dateForFilter: string;
  } | null>(null);
  const { api } = useAxios();

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  useEffect(() => {
    localStorage.removeItem("ReportDateFilter");
    // We need setTimeout becouse on componentDidMount ref current is undefined, it needs short time to intialize
    const scrollTimer = setTimeout(() => {
      scrollRef?.current?.scrollBy({
        top: 800
      });
    }, 500);
    return () => clearTimeout(scrollTimer);
  }, []);

  const locationIdsString = JSON.stringify(locationIds);

  const locations = permissions.isArtist
    ? user?.joined_company?.locations
    : user?.company?.locations;
  const timezone = locations?.[0]?.timezone || "PST8PDT";

  const startTime = getStartAndEndTime(
    selectedCalendarDate?.dateForFilter,
    selectedView,
    timezone
  );

  const startTimeString = JSON.stringify(startTime);

  const onExport = () => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`
    };

    const url = `/v1/reporting/tipping?locationIds=${locationIdsString}&startTime=${startTimeString}`;

    api.get(url, { headers }).then((response: any) => {
      const data = response.data.data;
      const columns = response.data.columns;
      let csv = columns.join(",") + "\n";
      data.forEach((row: any) => {
        csv += columns.map((column: string) => row[column]).join(",");
        csv += "\n";
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tipping_${selectedCalendarDate?.title}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative"
      }}
    >
      <CustomReportingCalendar
        calendarsDate={calendarsDate}
        setCalendarsDate={setCalendarsDate}
        calendarsDateTitle={selectedCalendarDate?.title || ""}
        setSelectedCalendarDate={setSelectedCalendarDate}
        setSelectedView={setSelectedView}
        selectedView={selectedView}
        timezone={timezone}
      />
      <CustomList
        empty={false}
        title={isSmall ? "" : translate("report.tipping")}
        hasCreate={false}
        exporter={false}
        queryOptions={{ meta: { startTimeString, locationIdsString } }}
        customExport={
          <Button label="ra.action.export" onClick={onExport}>
            <DownloadIcon />
          </Button>
        }
        key={location.search}
        reportingListsWidth="calc(100vw - 34px)"
      >
        <CustomDataGrid
          rowClick={false}
          hover={false}
          empty={
            <div
              style={{
                height: "calc(100vh - 200px)",
                backgroundColor: "#F6F4F1"
              }}
            >
              <CustomEmptyList
                emptyIcon={<EmptyReportIcon />}
                emptyTitle={translate("empty_list.no_results_found")}
                emptyListCreateSubtitle=""
                createButtonText=""
                hasCreate={false}
              />
            </div>
          }
          sx={{
            borderRadius: "0px",
            ".RaDatagrid-headerCell": {
              backgroundColor: "transparent",
              minWidth: "0px",
              fontSize: "14px",
              lineHeight: "18px",
              "@media screen and (max-width:1280px)": {
                fontSize: "12px"
              },
              "@media screen and (max-width:968px)": {
                paddingLeft: "8px",
                paddingRight: "8px"
              }
            },
            ".RaDatagrid-rowCell": {
              minWidth: "0px",
              padding: "10px 16px",
              "@media screen and (max-width:968px)": {
                paddingLeft: "8px",
                paddingRight: "8px"
              }
            },
            ".RaDatagrid-rowCell span": {
              fontSize: "14px",
              "@media screen and (max-width:1280px)": {
                fontSize: "12px"
              }
            }
          }}
        >
          <FunctionField
            label={translate("report.order_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.orderId}</div>
            )}
          />
          <FunctionField
            label={translate("report.artist_name")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.artistName}</div>
            )}
          />
          <FunctionField
            label={translate("report.artist_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.artistId}</div>
            )}
          />
          <FunctionField
            label={translate("report.appointment_date")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {dayjs(
                  getZonedDate(record?.appointmentDate, record.timezone)
                ).format("MM/DD/YYYY hh:mm A")}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.appointment_description")}
            render={(record: any) => (
              <div className="capitalize flex flex-col">
                {record?.appointmentDescription
                  ?.split(" | ")
                  ?.map((service: string, i: number) => (
                    <span className="whitespace-nowrap" key={service + i}>
                      {service}
                    </span>
                  ))}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.customer_name")}
            render={(record: any) => (
              <div className="line-clamp-1 break-all min-w-[150px]">
                {record?.customerName}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.tip_amount")}
            render={(record: any) =>
              record?.tipAmount ? `$${record?.tipAmount?.toFixed(2)}` : `$0.00`
            }
          />
        </CustomDataGrid>
      </CustomList>
    </Box>
  );
}
